/**
 * US-153 / F-134 — the shared reach for "a record that can complete a derived
 * task was written".
 *
 * The resident "Today's Tasks" board is READ-ONLY by design: its items complete
 * when the underlying clinical work is recorded, and the server recomputes them
 * on every load. So the write path that records that work is the only thing that
 * can tell the board it changed.
 *
 * Seven derived kinds hang off five source tables — medications, vitals, ADL,
 * care notes (both `check_in` and `shift_note`), appointments and forms — and
 * before this helper existed, exactly ONE of those write paths invalidated the
 * board. Recording vitals left it reading "Not yet recorded" about work the user
 * had just done, until a manual browser reload.
 *
 * Two correct implementations of this reach already existed and neither was
 * reachable from the clinical hooks that needed it: a private helper in
 * useTaskSubscriptions.ts and a hand-rolled copy in useMyTasks.ts. This is the
 * one both now delegate to.
 */
import type { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate every surface that derives from task-completing records.
 *
 * UNKEYED ON PURPOSE. The resident id is not known at every call site — a shift
 * note is written without the board's `residentId` in scope — so this refetches
 * all mounted task surfaces rather than one. That is the deliberate trade: these
 * are small reads, and the alternative is threading a subject id through five
 * hooks, which is precisely the hand-maintained reach this helper removes. The
 * same choice is already made, with the same reasoning, at useMyTasks.ts.
 *
 * Does NOT touch ["task-subscriptions", facilityId] — that is the facility's
 * task CONFIG catalog, not work derived from records. useTaskSubscriptions.ts
 * adds it on top for config writes.
 */
export function invalidateTaskSurfaces(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["my-tasks"] });
  qc.invalidateQueries({ queryKey: ["facility-task-dashboard"] });
  qc.invalidateQueries({ queryKey: ["resident-today-tasks"] });
  // SCRUM-231: the resident activity timeline is fed by the SAME writes as the
  // task board, so it rides along here rather than growing a second reach for
  // one caller to forget. It is not itself a task surface — see below.
  invalidateResidentTimeline(qc);
}

/**
 * SCRUM-231 — refresh the resident activity timeline (`useResidentTimeline`).
 *
 * Separate from the task board because the write sets only mostly overlap: an
 * incident appears on the timeline but completes no derived task, so
 * useIncidents calls this directly rather than invalidating three task surfaces
 * it has no business touching.
 *
 * UNKEYED on the resident, for the same reason invalidateTaskSurfaces is: the id
 * is not in scope at every call site (useCreateIncident carries it in the
 * payload), and narrowing to `[key, residentId]` would silently drop exactly
 * those paths. Nor can it be keyed on the query's full shape — the Overview card
 * caches under `[key, id, null, null]` and its "View All" modal under
 * `[key, id, 90, 200]`, two entries of the same feed. A prefix reach reaches
 * both; anything narrower lets the two views disagree, which is the symptom
 * SCRUM-231 was reported as.
 */
export function invalidateResidentTimeline(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["resident-timeline"] });
}
